import React, { useState, useEffect } from 'react';
import { ArrowRight, Users, Calendar, CheckCircle, AlertTriangle, TrendingUp, History } from 'lucide-react';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';

const StudentPromotion = () => {
  const [classes, setClasses] = useState([]);
  const [selectedFromClass, setSelectedFromClass] = useState('');
  const [toClass, setToClass] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    fetchClasses();
    // Set default academic year and effective date
    const currentYear = new Date().getFullYear();
    setAcademicYear(`${currentYear}-${currentYear + 1}`);
    setEffectiveDate(new Date().toISOString().split('T')[0]);
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await apiClient.get('/v1/admin/promotions/classes');
      setClasses(response.data.classes || []);
    } catch (error) {
      toast.error('Failed to load classes');
    }
  };

  const handlePreview = async () => {
    if (!selectedFromClass || !toClass || !academicYear) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/v1/admin/promotions/preview', {
        from_class: selectedFromClass,
        to_class: toClass,
        academic_year: academicYear
      });

      setPreviewData(response.data);
      // Select all students by default
      setSelectedStudents(response.data.students.map(s => s.id));
      
      if (!response.data.to_class_exists) {
        toast.warning(`Target class "${toClass}" does not exist. Please create it first.`);
      } else {
        toast.success(`Found ${response.data.total_students} students to promote`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview promotion');
      setPreviewData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (previewData) {
      setSelectedStudents(previewData.students.map(s => s.id));
    }
  };

  const handleDeselectAll = () => {
    setSelectedStudents([]);
  };

  const toggleStudent = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleExecutePromotion = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student to promote');
      return;
    }

    setPromoting(true);
    try {
      const response = await apiClient.post('/v1/admin/promotions/execute', {
        from_class: selectedFromClass,
        to_class: toClass,
        student_ids: selectedStudents,
        academic_year: academicYear,
        effective_date: effectiveDate
      });

      toast.success(
        `✅ Successfully promoted ${response.data.promoted_count} student(s) to ${toClass}!`
      );

      if (response.data.errors.length > 0) {
        toast.warning(`⚠️ ${response.data.errors.length} error(s) occurred`);
      }

      // Reset form
      setShowConfirmation(false);
      setPreviewData(null);
      setSelectedStudents([]);
      setSelectedFromClass('');
      setToClass('');
      
      // Refresh classes
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute promotion');
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#2C5F7F' }}>
          Student Promotion / Class Increment
        </h2>
        <p className="text-gray-600">
          Promote students to the next class with academic year tracking
        </p>
      </div>

      {/* Configuration Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Step 1: Configure Promotion
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* From Class */}
          <div>
            <label className="block text-sm font-medium mb-2">From Class *</label>
            <select
              value={selectedFromClass}
              onChange={(e) => {
                setSelectedFromClass(e.target.value);
                // Auto-suggest next class
                if (e.target.value) {
                  const classNum = parseInt(e.target.value.match(/\d+/)?.[0]);
                  if (classNum) {
                    setToClass(e.target.value.replace(/\d+/, classNum + 1));
                  }
                }
                setPreviewData(null);
              }}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select Class</option>
              {classes.map((cls) => (
                <option key={cls.id} value={cls.name}>
                  {cls.name} ({cls.student_count} students)
                </option>
              ))}
            </select>
          </div>

          {/* To Class */}
          <div>
            <label className="block text-sm font-medium mb-2">To Class *</label>
            <input
              type="text"
              value={toClass}
              onChange={(e) => {
                setToClass(e.target.value);
                setPreviewData(null);
              }}
              placeholder="e.g., Class 7"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Target class will be auto-suggested
            </p>
          </div>

          {/* Academic Year */}
          <div>
            <label className="block text-sm font-medium mb-2">Academic Year *</label>
            <input
              type="text"
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              placeholder="e.g., 2025-2026"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Effective Date */}
          <div>
            <label className="block text-sm font-medium mb-2">Effective Date *</label>
            <input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <button
          onClick={handlePreview}
          disabled={loading || !selectedFromClass || !toClass || !academicYear}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors flex items-center gap-2"
        >
          <Users className="w-4 h-4" />
          {loading ? 'Loading...' : 'Preview Students'}
        </button>
      </div>

      {/* Preview Section */}
      {previewData && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Step 2: Select Students to Promote
            </h3>
            <div className="flex gap-2">
              <button
                onClick={handleSelectAll}
                className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
              >
                Select All
              </button>
              <button
                onClick={handleDeselectAll}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Deselect All
              </button>
            </div>
          </div>

          {/* Promotion Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div>
              <p className="text-sm text-gray-600">From</p>
              <p className="font-semibold text-lg">{previewData.from_class}</p>
            </div>
            <div className="flex items-center justify-center">
              <ArrowRight className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">To</p>
              <p className="font-semibold text-lg">{previewData.to_class}</p>
              {!previewData.to_class_exists && (
                <p className="text-xs text-orange-600 mt-1">⚠️ Class does not exist</p>
              )}
            </div>
          </div>

          {/* Warning if target class doesn't exist */}
          {!previewData.to_class_exists && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-yellow-800">Target Class Not Found</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    The class &quot;{previewData.to_class}&quot; does not exist in the system. 
                    Please create it first before promoting students.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Students List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {previewData.students.map((student) => (
              <div
                key={student.id}
                onClick={() => toggleStudent(student.id)}
                className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-all ${
                  selectedStudents.includes(student.id)
                    ? 'bg-green-50 border-green-300'
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    selectedStudents.includes(student.id)
                      ? 'bg-green-600 border-green-600'
                      : 'border-gray-300'
                  }`}>
                    {selectedStudents.includes(student.id) && (
                      <CheckCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{student.name}</p>
                    <p className="text-sm text-gray-600">
                      ID: {student.readable_id} | Room: {student.room_number || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between pt-4 border-t">
            <p className="text-sm text-gray-600">
              Selected: <span className="font-bold text-blue-600">{selectedStudents.length}</span> / {previewData.total_students} students
            </p>
            <button
              onClick={() => setShowConfirmation(true)}
              disabled={selectedStudents.length === 0 || !previewData.to_class_exists}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors flex items-center gap-2"
            >
              <TrendingUp className="w-4 h-4" />
              Promote Selected Students
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Student Promotion</h3>
                <p className="text-sm text-gray-600 mb-2">
                  You are about to promote <span className="font-bold text-green-600">{selectedStudents.length}</span> student(s) from:
                </p>
                <div className="p-3 bg-gray-50 rounded-lg space-y-1 text-sm">
                  <p><span className="font-semibold">From:</span> {selectedFromClass}</p>
                  <p><span className="font-semibold">To:</span> {toClass}</p>
                  <p><span className="font-semibold">Academic Year:</span> {academicYear}</p>
                  <p><span className="font-semibold">Effective Date:</span> {effectiveDate}</p>
                </div>
                <p className="text-sm text-gray-600 mt-3">
                  <strong>Note:</strong> Student records will be preserved. Class history will be tracked.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleExecutePromotion}
                disabled={promoting}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors font-medium"
              >
                {promoting ? 'Promoting...' : 'Yes, Promote'}
              </button>
              <button
                onClick={() => setShowConfirmation(false)}
                disabled={promoting}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:bg-gray-400 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <History className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">How Class Promotion Works:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Student&apos;s current class is updated to the new class</li>
              <li>All historical records (attendance, health, results) remain unchanged</li>
              <li>Class history is tracked with academic years for progression reports</li>
              <li>Parents can see complete history across all classes</li>
              <li>Warden assignments are preserved unless manually changed</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentPromotion;
